import {
  forwardRef,
  useMemo,
  type PropsWithChildren,
  type ReactNode,
  Children,
} from 'react';
import { useIntl } from 'react-intl';
import { isElement } from 'react-is';

import { Button } from '@dynatrace/strato-components/buttons';
import type {
  AriaLabelingProps,
  AriaDisabledProps,
  DataTestId,
  DOMProps,
  MaskingProps,
  StylingProps,
} from '@dynatrace/strato-components/core';
import { Container, Flex } from '@dynatrace/strato-components/layouts';
import { XmarkIcon } from '@dynatrace/strato-icons';

import { Actions } from './Actions.js';
import { MessageContainerContext } from './contexts/MessageContainerContext.js';
import { Description } from './Description.js';
import { Prefix } from './Prefix.js';
import { Title } from './Title.js';
import { isDev } from '../../core/utils/is-node-environment.js';

// #region Typings

/**
 * Accepted properties for MessageContainer
 * @public
 */
export interface MessageContainerProps
  extends DOMProps,
    StylingProps,
    DataTestId,
    MaskingProps,
    AriaLabelingProps,
    AriaDisabledProps,
    PropsWithChildren {
  /** Defines the type of the panel. */
  variant?: 'neutral' | 'primary' | 'success' | 'warning' | 'critical';
  /** Handler that is called if the panel gets dismissed. */
  onDismiss?: () => void;
}
// #endregion

const getSlots = (children: ReactNode) => {
  const slots: {
    prefix: ReactNode;
    title: ReactNode;
    description: ReactNode;
    actions: ReactNode;
  } = {
    prefix: null,
    title: null,
    description: null,
    actions: null,
  };

  Children.forEach(children, (child) => {
    if (!isElement(child)) {
      if (child && isDev()) {
        // eslint-disable-next-line no-console
        console.warn('Unsupported child.');
      }
      return;
    }

    switch (child.type) {
      case Prefix:
        slots.prefix = child;
        break;
      case Title:
        slots.title = child;
        break;
      case Description:
        slots.description = child;
        break;
      case Actions:
        slots.actions = child;
        break;
      default:
        if (isDev()) {
          // eslint-disable-next-line no-console
          console.warn(
            'The used child is not a valid child of the MessageContainer component.',
          );
        }
        break;
    }
  });

  if (!slots.title && isDev()) {
    // eslint-disable-next-line no-console
    console.warn('The MessageContainer component requires a Title component.');
  }

  return slots;
};

const _MessageContainer = forwardRef<HTMLDivElement, MessageContainerProps>(
  (props, forwardedRef) => {
    const {
      children,
      className: consumerClassName,
      style: consumerStyle,
      id,
      'data-dtrum-allow': dataDtrumAllow,
      'data-dtrum-mask': dataDtrumMask,
      variant = 'neutral',
      onDismiss,
      ...remainingProps
    }: MessageContainerProps = props;

    const intl = useIntl();

    const slots = useMemo(() => getSlots(children), [children]);
    const isSingleline = !slots.description;

    return (
      <MessageContainerContext.Provider value={{ variant }}>
        <Container
          color={variant}
          padding={6}
          paddingLeft={16}
          id={id}
          ref={forwardedRef}
          data-dtrum-allow={dataDtrumAllow}
          data-dtrum-mask={dataDtrumMask}
          style={consumerStyle}
          className={consumerClassName}
          {...remainingProps}
        >
          <Flex justifyContent="space-between" alignItems="flex-start">
            {slots.prefix && (
              <Flex flex="0 0 20px" paddingY={6} justifyContent="center">
                {slots.prefix}
              </Flex>
            )}
            <Flex
              flexGrow={1}
              columnGap={8}
              rowGap={6}
              flexDirection={isSingleline ? 'row' : 'column'}
              flexWrap="wrap"
              minWidth={0}
            >
              <Flex
                flexDirection="column"
                gap={2}
                flexGrow={1}
                justifyContent="center"
                paddingY={6}
                paddingRight={8}
                maxWidth="100%"
                minWidth={0}
                style={{ boxSizing: 'border-box' }}
              >
                {slots.title}
                {slots.description}
              </Flex>
              {slots.actions && (
                <Flex
                  style={{
                    paddingRight: slots.description ? 8 : 0,
                    paddingBottom: slots.description ? 10 : 0,
                  }}
                >
                  {slots.actions}
                </Flex>
              )}
            </Flex>
            {onDismiss !== undefined && (
              <Flex flexShrink={0} alignItems="flex-start">
                <Button
                  onClick={onDismiss}
                  color={variant}
                  aria-label={intl.formatMessage({
                    defaultMessage: 'Close message',
                    id: '2+ypcBK9NuQ5zg1k',
                    description:
                      'Aria label used for the close button of the information',
                  })}
                >
                  <Button.Prefix>
                    <XmarkIcon />
                  </Button.Prefix>
                </Button>
              </Flex>
            )}
          </Flex>
        </Container>
      </MessageContainerContext.Provider>
    );
  },
);

(
  _MessageContainer as typeof _MessageContainer & { displayName: string }
).displayName = 'MessageContainer';

/**
 * The `MessageContainer` allows you to communicate statuses prominently. It's either persistent or dismissible. If needed, provide actions to help users take the following steps or resolve an issue.
 * @public
 */
export const MessageContainer = Object.assign(_MessageContainer, {
  Prefix,
  Title,
  Description,
  Actions,
});
